import { onUnmounted, Ref, ref } from 'vue';

type HeightGroup = {
    height: Ref<number>;
    minHeight: number;
    reportedHeight: number;
    componentCount: number;
};

// Global store for height groups
const heightGroups = new Map<string, HeightGroup>();

export function useHeightSync(groupId: string, minHeightInRem = 0) {
    if (!heightGroups.has(groupId)) {
        heightGroups.set(groupId, {
            height: ref(minHeightInRem),
            minHeight: minHeightInRem,
            reportedHeight: 0,
            componentCount: 0,
        });
    }
    const group = heightGroups.get(groupId)!;

    // Raise the group's floor to the largest minimum any member requests, regardless of mount order.
    if (minHeightInRem > group.minHeight) {
        group.minHeight = minHeightInRem;
        group.height.value = Math.max(group.minHeight, group.reportedHeight);
    }

    group.componentCount++;

    // Records the reported content height and exposes it clamped to the group's floor.
    const setHeight = (height: number): void => {
        group.reportedHeight = height;
        group.height.value = Math.max(group.minHeight, height);
    };

    onUnmounted(() => {
        group.componentCount--;

        if (group.componentCount === 0) {
            heightGroups.delete(groupId);
        }
    });

    return {
        heightInRem: group.height,
        setHeight,
    };
}
